import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DoCheck,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  Self,
  SimpleChanges,
  ViewChild,
  forwardRef,
} from '@angular/core';
import { environment } from 'src/environments/environment';
import {
  ControlValueAccessor,
  FormControl,
  FormGroupDirective,
  NgControl,
  NgForm,
  Validators,
} from '@angular/forms';
import { ErrorStateMatcher } from 'src/app/components/form-field/error-state-matcher';
import { FormFieldControl } from 'src/app/components/form-field/form-field.types';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  AutocompleteHint,
  InputMode,
  InputType,
  ResizeMode,
} from './input.types';

let uniqueIdCounter = 0;

/**
 * Form-field control living inside `<app-form-field>` (shell still owns the
 * outline / float-label / hint chrome). Wraps a native `<input>` /
 * `<textarea>` and bridges its value through an internal `FormControl` so
 * ngx-mask — itself a CVA on the inner element — manages the formatted
 * display while we propagate the clean value out to the outer form.
 *
 * Subscription-ordering: the `_innerControl.valueChanges` subscription is
 * wired in `ngAfterViewInit`, not in the constructor. ngx-mask's `writeValue`
 * is async (`Promise<void>`); subscribing later ensures the directive's
 * clean-value writeback wins over the raw display emission.
 */
@Component({
  selector: 'app-input',
  exportAs: 'appInput',
  templateUrl: './input.component.html',
  styleUrls: ['./input.component.scss'],
  host: {
    class: 'app-input',
    '[class.app-input--disabled]': 'disabled',
    '[class.app-input--readonly]': 'readonly',
    '[class.app-input--invalid]': 'errorState',
    '[class.app-input--focused]': 'focused',
    '[class.app-input--multiline]': 'multiline',
    // Intentionally NO `[attr.id]` on the host: the bare `id` lands on the
    // focusable inner element (see template). `<app-form-field>` reads
    // `FormFieldControl.id` to build `<label for=...>`; pointing it at
    // the inner element makes the label natively focus the input on click.
    '(focusin)': '_onFocusIn()',
    '(focusout)': '_onFocusOut($event)',
  },
  providers: [
    {
      provide: FormFieldControl,
      useExisting: forwardRef(() => InputComponent),
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
// The inner element is always a native `<input>` / `<textarea>` with a
// string `.value`; the inner FormControl is `FormControl<string | null>`.
// When `type="number"` we coerce the string back to a number at the CVA
// boundary so consumers get the same contract Angular's NumberValueAccessor
// provides on plain `<input type="number">`.
export class InputComponent
  implements
    ControlValueAccessor,
    FormFieldControl<string | number | null>,
    OnInit,
    OnChanges,
    OnDestroy,
    DoCheck,
    AfterViewInit
{
  // ---------- identity ----------
  readonly controlType = 'app-input';
  readonly stateChanges = new Subject<void>();
  readonly autofilled = false;

  private _id = `app-input-${uniqueIdCounter++}`;
  @Input()
  get id(): string {
    return this._id;
  }
  set id(value: string) {
    this._id = value || `app-input-${uniqueIdCounter++}`;
    this._cdr?.markForCheck();
    this.stateChanges.next();
  }

  // ---------- basic attributes (passthrough) ----------
  @Input() name: string | null = null;
  @Input() placeholder = '';
  @Input() type: InputType = 'text';
  @Input() autocomplete: AutocompleteHint = 'off';
  @Input() inputmode: InputMode | null = null;
  @Input() min: number | string | null = null;
  @Input() max: number | string | null = null;
  @Input() step: number | string | null = null;
  @Input() minlength: number | null = null;
  @Input() maxlength: number | null = null;
  @Input() pattern: string | null = null;

  // ---------- textarea variant ----------
  private _multiline = false;
  @Input()
  get multiline(): boolean {
    return this._multiline;
  }
  set multiline(value: BooleanInput) {
    this._multiline = coerceBooleanProperty(value);
  }

  @Input() rows = 3;
  @Input() minRows: number | null = null;
  @Input() maxRows: number | null = null;
  @Input() resize: ResizeMode = 'vertical';

  private _autosize = false;
  @Input()
  get autosize(): boolean {
    return this._autosize;
  }
  set autosize(value: BooleanInput) {
    this._autosize = coerceBooleanProperty(value);
  }

  private _autofocus = false;
  @Input()
  get autofocus(): boolean {
    return this._autofocus;
  }
  set autofocus(value: BooleanInput) {
    this._autofocus = coerceBooleanProperty(value);
  }

  // ---------- ngx-mask passthrough ----------
  // Use `[mask]` for static patterns; `[imask]` below for content-dispatch
  // alternation. The two are mutually exclusive.
  @Input() mask: string | null = null;
  @Input() thousandSeparator: string | null = null;
  @Input() decimalMarker: '.' | ',' | ['.', ','] | null = null;
  @Input() prefix = '';
  @Input() suffix = '';

  private _dropSpecialCharacters: boolean | string[] = true;
  @Input()
  get dropSpecialCharacters(): boolean | string[] {
    return this._dropSpecialCharacters;
  }
  set dropSpecialCharacters(value: BooleanInput | string[]) {
    this._dropSpecialCharacters = Array.isArray(value)
      ? value
      : coerceBooleanProperty(value);
  }

  // ---------- angular-imask passthrough ----------
  // Use `[imask]` for content-dispatch alternation (ngx-mask only dispatches
  // by length). Config shape follows IMask's `AnyMaskedOptions`.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Input() imask: any = null;
  @Input() unmask: boolean | 'typed' = true;

  // ---------- disabled / readonly / required ----------
  private _disabled = false;
  // True if either `[disabled]` was set OR `ngControl.disabled` is true.
  // The inner FormControl is synced through `_syncInnerDisabled()` from
  // the setter, `setDisabledState`, and the `statusChanges` subscription
  // — the last covers side-channel disables that bypass the other two.
  @Input()
  get disabled(): boolean {
    return this._disabled || this.ngControl?.disabled === true;
  }
  set disabled(value: BooleanInput) {
    this._disabled = coerceBooleanProperty(value);
    this._syncInnerDisabled();
    this._cdr.markForCheck();
    this.stateChanges.next();
  }

  private _readonly = false;
  @Input()
  get readonly(): boolean {
    return this._readonly;
  }
  set readonly(value: BooleanInput) {
    this._readonly = coerceBooleanProperty(value);
    this._cdr.markForCheck();
    this.stateChanges.next();
  }

  private _required = false;
  @Input()
  get required(): boolean {
    return (
      this._required ||
      this.ngControl?.control?.hasValidator(Validators.required) === true
    );
  }
  set required(value: BooleanInput) {
    this._required = coerceBooleanProperty(value);
    this.stateChanges.next();
  }

  // ---------- error state ----------
  @Input() errorStateMatcher?: ErrorStateMatcher;

  // ---------- a11y ----------
  // eslint-disable-next-line @angular-eslint/no-input-rename
  @Input('aria-label') ariaLabel: string | null = null;
  // eslint-disable-next-line @angular-eslint/no-input-rename
  @Input('aria-labelledby') ariaLabelledby: string | null = null;
  // Combobox forwarding: lets <app-input> act as the trigger of an external
  // listbox overlay (e.g. <app-autocomplete> in embedded mode).
  @Input() role: string | null = null;
  // eslint-disable-next-line @angular-eslint/no-input-rename
  @Input('aria-autocomplete') ariaAutocomplete: string | null = null;
  // eslint-disable-next-line @angular-eslint/no-input-rename
  @Input('aria-expanded') ariaExpanded: boolean | string | null = null;
  // eslint-disable-next-line @angular-eslint/no-input-rename
  @Input('aria-controls') ariaControls: string | null = null;
  // eslint-disable-next-line @angular-eslint/no-input-rename
  @Input('aria-activedescendant') ariaActivedescendant: string | null = null;

  // ---------- value (two-way) ----------
  @Input()
  get value(): string | number | null {
    return this._publicValue(this._innerControl.value);
  }
  set value(newValue: string | number | null) {
    const normalized = newValue == null ? null : String(newValue);
    if (normalized !== this._innerControl.value) {
      this._innerControl.setValue(normalized, { emitEvent: false });
      this._cdr.markForCheck();
      this.stateChanges.next();
    }
  }

  // ---------- outputs ----------
  @Output() readonly valueChange = new EventEmitter<string | number | null>();
  @Output() readonly focusChange = new EventEmitter<boolean>();
  @Output() readonly inputEvent = new EventEmitter<Event>();
  @Output() readonly blurEvent = new EventEmitter<FocusEvent>();

  // ---------- queries ----------
  @ViewChild('inner', { static: false })
  _innerRef?: ElementRef<HTMLInputElement | HTMLTextAreaElement>;

  // ---------- FormFieldControl state ----------
  focused = false;
  _userAriaDescribedBy = '';

  get empty(): boolean {
    const v = this._innerControl.value;
    return v === null || v === undefined || v === '';
  }

  get shouldLabelFloat(): boolean {
    return this.focused || !this.empty;
  }

  get hostElement(): HTMLElement {
    return this._elementRef.nativeElement;
  }

  get errorState(): boolean {
    return this._errorState;
  }

  // ---------- internal state ----------
  /**
   * Bridge to the inner element. ngx-mask writes the clean value here via
   * its CVA; we mirror `valueChanges` out so the outer FormControl receives
   * the clean value, not the formatted display.
   *
   * @internal — exposed without `_` for template / spec access.
   */
  _innerControl = new FormControl<string | null>(null);

  private _errorState = false;
  private _onChange: (value: string | number | null) => void = () => {};
  private _onTouched: () => void = () => {};
  private readonly _destroy$ = new Subject<void>();

  // ---------- lifecycle ----------
  constructor(
    private _elementRef: ElementRef<HTMLElement>,
    private _cdr: ChangeDetectorRef,
    @Optional() private _defaultErrorStateMatcher: ErrorStateMatcher | null,
    @Optional() @Self() public ngControl: NgControl | null,
    @Optional() private _parentForm: NgForm | null,
    @Optional() private _parentFormGroup: FormGroupDirective | null
  ) {
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  ngAfterViewInit(): void {
    // Subscribing here (not in ctor) so ngx-mask has applied its CVA on
    // the inner element first — see class doc.
    this._innerControl.valueChanges
      .pipe(takeUntil(this._destroy$))
      .subscribe((raw) => {
        const value = this._publicValue(raw);
        this._onChange(value);
        this.valueChange.emit(value);
        this._updateErrorState();
        this.stateChanges.next();
        this._cdr.markForCheck();
      });

    this.ngControl?.statusChanges
      ?.pipe(takeUntil(this._destroy$))
      .subscribe(() => {
        this._updateErrorState();
        // Catches side-channel disables that bypass the `[disabled]` setter
        // and `setDisabledState` (e.g. parent FormGroup propagation).
        this._syncInnerDisabled();
        // OnPush: getters `disabled` / `required` read ngControl directly,
        // so they need a CD nudge when the parent flips state silently.
        this._cdr.markForCheck();
      });

    this._syncInnerDisabled();
  }

  ngOnInit(): void {
    // Modern browsers ignore `autocomplete="off"` on credential fields,
    // so password-manager autofill is silently lost. Consumers must opt
    // into specific tokens (`username`, `current-password`, etc.).
    if (
      !environment.production &&
      (this.type === 'email' || this.type === 'password') &&
      this.autocomplete === 'off'
    ) {
      // eslint-disable-next-line no-console
      console.warn(
        `<app-input type="${this.type}" autocomplete="off"> — password ` +
          `managers ignore "off" in modern browsers. Use "username", ` +
          `"current-password", or "new-password" explicitly.`
      );
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['disabled'] || changes['required'] || changes['placeholder']) {
      this.stateChanges.next();
    }
    // Dynamic type swaps (e.g. dynamic-form's `[type]="field.inputType"`)
    // must re-coerce the current value through the new type's path so the
    // outer FormControl receives a consistent shape. Skip the first change
    // — the initial subscription in ngAfterViewInit covers it. Deferred to
    // a microtask so the re-emission does not mutate parent-bound state
    // mid-CD (would trigger ExpressionChangedAfterItHasBeenCheckedError).
    if (changes['type'] && !changes['type'].firstChange) {
      queueMicrotask(() => {
        const value = this._publicValue(this._innerControl.value);
        this._onChange(value);
        this.valueChange.emit(value);
        this.stateChanges.next();
        this._cdr.markForCheck();
      });
    }
    // The template gates the masked branch via `*ngIf="!mask"`. Flipping
    // `mask` at runtime swaps the inner `<input>` element and drops focus
    // / selection — surfaced via the dev warn below.
    //
    // Two branches (not a single `[mask]="mask"`) because NgxMaskDirective's
    // selector `input[mask]` matches by attribute presence regardless of
    // value: a single branch would keep ngx-mask attached when mask is
    // null, and its async `writeValue` races the first programmatic write.
    if (
      changes['mask'] &&
      !changes['mask'].firstChange &&
      !environment.production
    ) {
      // eslint-disable-next-line no-console
      console.warn(
        `<app-input> mask changed at runtime — the *ngIf branch swap drops ` +
          `focus and selection on the inner element. If you need dynamic masks ` +
          `keep the mask Input static at the consumer level.`
      );
    }
    if (
      (changes['imask'] || changes['mask']) &&
      this.mask &&
      this.imask &&
      !environment.production
    ) {
      // eslint-disable-next-line no-console
      console.warn(
        `<app-input> [mask] and [imask] are mutually exclusive; [imask] wins. ` +
          `Drop one Input on the consumer template.`
      );
    }
  }

  ngDoCheck(): void {
    // `disabled` / `touched` aren't exposed as observables on NgControl;
    // mirror mat-input's pattern and re-evaluate error state once per CD.
    if (this.ngControl) {
      this._updateErrorState();
    }
  }

  ngOnDestroy(): void {
    // Subscriptions piped through `takeUntil(this._destroy$)` unsubscribe
    // automatically when the subject emits; no explicit `.unsubscribe()`
    // call is needed for them. The Subject itself must still be completed
    // to release downstream consumers.
    this._destroy$.next();
    this._destroy$.complete();
    this.stateChanges.complete();
    this.valueChange.complete();
    this.focusChange.complete();
    this.inputEvent.complete();
    this.blurEvent.complete();
  }

  // ---------- ControlValueAccessor ----------
  // `writeValue` signature is `(obj: any): void` per the CVA interface;
  // Angular Forms may invoke it with any FormControl value type the
  // consumer chose (e.g. `number` from a `FormControl<number>`). Coerce
  // to `string | null` for the inner control — non-string values become
  // their `String()` form so `type="number"` consumers don't lose data.
  writeValue(value: unknown): void {
    if (
      value != null &&
      typeof value !== 'string' &&
      typeof value !== 'number' &&
      !environment.production
    ) {
      // eslint-disable-next-line no-console
      console.warn(
        `InputComponent.writeValue: unsupported value type "${typeof value}"; ` +
          `coercing via String(). Pass string | number | null instead.`
      );
    }
    // Non-finite numbers (NaN, ±Infinity) coerce to null instead of "NaN"
    // / "Infinity" in the DOM; mirrors `_publicValue` on the emission side.
    const normalized: string | null =
      value == null
        ? null
        : typeof value === 'string'
          ? value
          : typeof value === 'number' && !Number.isFinite(value)
            ? null
            : String(value);
    this._innerControl.setValue(normalized, { emitEvent: false });
    this._cdr.markForCheck();
    // Programmatic writes (FormControl.setValue / .reset) must wake the
    // surrounding `<app-form-field>` so it re-evaluates `empty` /
    // `shouldLabelFloat` — otherwise the floating label can stick after
    // a reset until the next focus / value change.
    this.stateChanges.next();
  }

  registerOnChange(fn: (value: string | number | null) => void): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this._disabled = isDisabled;
    this._syncInnerDisabled();
    this._cdr.markForCheck();
    this.stateChanges.next();
  }

  // ---------- FormFieldControl ----------
  setDescribedByIds(ids: string[]): void {
    this._userAriaDescribedBy = ids.length ? ids.join(' ') : '';
    this._cdr.markForCheck();
  }

  onContainerClick(): void {
    // Readonly fields stay focusable so keyboard nav works; the SCSS
    // `cursor: default` is hover-only and intentionally diverges from
    // the focused state.
    if (!this.disabled) {
      this.focus();
    }
  }

  // ---------- imperative API ----------
  focus(options?: FocusOptions): void {
    this._innerRef?.nativeElement.focus(options);
  }

  blur(): void {
    this._innerRef?.nativeElement.blur();
  }

  // ---------- inner event handlers ----------
  _onFocusIn(): void {
    if (!this.disabled && !this.focused) {
      this.focused = true;
      this.focusChange.emit(true);
      this.stateChanges.next();
    }
  }

  _onFocusOut(event: FocusEvent): void {
    const nextFocus = event.relatedTarget as Node | null;
    if (!nextFocus || !this._elementRef.nativeElement.contains(nextFocus)) {
      this.focused = false;
      this._onTouched();
      this.focusChange.emit(false);
      this.blurEvent.emit(event);
      this._updateErrorState();
      this.stateChanges.next();
    }
  }

  _handleInput(event: Event): void {
    // The `_innerControl.valueChanges` subscription is the single channel that
    // propagates inner edits to the outer CVA — both for plain input (where
    // Angular's default value accessor writes to the FormControl directly) and
    // for masked input (where ngx-mask rewrites the value through its own CVA
    // before publishing). This handler just re-emits the native event for
    // consumers listening via `(inputEvent)`.
    this.inputEvent.emit(event);
  }

  // ---------- internal helpers ----------
  /**
   * Coerces the inner `string | null` to the public value shape.
   * `type="number"` mirrors `NumberValueAccessor`: empty / non-finite
   * map to `null`. Other types pass through unchanged.
   */
  private _publicValue(raw: string | null): string | number | null {
    if (this.type !== 'number') {
      return raw;
    }
    if (raw == null || raw === '') {
      return null;
    }
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  }

  private _syncInnerDisabled(): void {
    const shouldDisable = this.disabled;
    if (shouldDisable && this._innerControl.enabled) {
      this._innerControl.disable({ emitEvent: false });
    } else if (!shouldDisable && this._innerControl.disabled) {
      this._innerControl.enable({ emitEvent: false });
    }
  }

  private _updateErrorState(): void {
    const matcher = this.errorStateMatcher ?? this._defaultErrorStateMatcher;
    const oldState = this._errorState;
    const parent = this._parentFormGroup ?? this._parentForm;
    const newState = matcher
      ? matcher.isErrorState(this.ngControl?.control ?? null, parent)
      : !!(this.ngControl?.invalid && this.ngControl?.touched);
    if (newState !== oldState) {
      this._errorState = newState;
      this.stateChanges.next();
      this._cdr.markForCheck();
    }
  }

  // Helper for the template to coerce `inputmode | null` into the
  // attribute-binding shape.
  get _innerAriaDescribedBy(): string | null {
    return this._userAriaDescribedBy || null;
  }
}
